/**
 * Why a browser could not produce a trusted-device proof locally.
 *
 * - `storageUnavailable` — IndexedDB/localStorage is missing, denied, or
 *   broken (private modes, some webviews, Safari "connection lost").
 * - `keyNotStorable` — storage works but rejects a `CryptoKey` value
 *   (`DataCloneError`).
 * - `storageQuota` — the browser refused the write (`QuotaExceededError`).
 * - `storageBlocked` — another tab holds the database; transient.
 * - `keyUnsupported` — ECDSA P-256 key generation is unavailable.
 * - `signingFailed` — a key exists but cannot sign or export its public key.
 * - `notEnrolled` — no stored installation and creating one was not allowed.
 *
 * @example
 * if (error.kind === "keyNotStorable") useExtractableKey();
 */
export type BrowserTrustedDeviceFailureKind =
  | "storageUnavailable"
  | "keyNotStorable"
  | "storageQuota"
  | "storageBlocked"
  | "keyUnsupported"
  | "signingFailed"
  | "notEnrolled";

/**
 * Local trusted-device failure tagged with a {@link BrowserTrustedDeviceFailureKind}.
 *
 * **Role.** Lets the login ladder decide — without parsing messages — whether
 * a failure is structural (try a fallback) or transient (show a retry hint).
 *
 * @example
 * throw new BrowserTrustedDeviceError("keyNotStorable", "CryptoKey cannot be stored", { cause });
 */
export class BrowserTrustedDeviceError extends Error {
  public readonly kind: BrowserTrustedDeviceFailureKind;

  public constructor(
    kind: BrowserTrustedDeviceFailureKind,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);

    this.name = "BrowserTrustedDeviceError";
    this.kind = kind;
  }
}

/**
 * Read the failure kind from an error, tolerating copies across bundles.
 *
 * @example
 * const kind = getBrowserTrustedDeviceFailureKind(error); // "storageQuota" | undefined
 */
export function getBrowserTrustedDeviceFailureKind(
  error: unknown,
): BrowserTrustedDeviceFailureKind | undefined {
  if (!error || typeof error !== "object") return undefined;

  const candidate = error as { name?: unknown; kind?: unknown };

  if (candidate.name !== "BrowserTrustedDeviceError") return undefined;

  return typeof candidate.kind === "string"
    ? (candidate.kind as BrowserTrustedDeviceFailureKind)
    : undefined;
}

/**
 * Map a raw storage `DOMException` to the failure kind it represents.
 *
 * @example
 * classifyStorageError(new DOMException("full", "QuotaExceededError")); // "storageQuota"
 */
export function classifyStorageError(
  error: unknown,
): BrowserTrustedDeviceFailureKind {
  const name =
    error && typeof error === "object" && "name" in error
      ? String((error as { name: unknown }).name)
      : "";

  if (name === "QuotaExceededError") return "storageQuota";

  if (name === "DataCloneError") return "keyNotStorable";

  return "storageUnavailable";
}

/**
 * Wrap a raw storage failure in a tagged {@link BrowserTrustedDeviceError}.
 *
 * @example
 * reject(toStorageFailure(request.error, "Trusted-device storage open failed"));
 */
export function toStorageFailure(
  error: unknown,
  fallbackMessage: string,
): BrowserTrustedDeviceError {
  if (error instanceof BrowserTrustedDeviceError) return error;

  const kind = classifyStorageError(error);
  const detail =
    error && typeof error === "object" && "name" in error
      ? `${String((error as { name: unknown }).name)}: ${String(
          (error as { message?: unknown }).message ?? "",
        )}`
      : "";

  return new BrowserTrustedDeviceError(
    kind,
    detail ? `${fallbackMessage} (${detail})` : fallbackMessage,
    { cause: error },
  );
}
